/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import {translate} from '@ion/core/src/locale/locale';
import {
  AssetType,
  Entity
} from '@ion/core/src/types/entities';
import {MemberSearchCriteria} from '@ion/core/src/types/services';
import BaseSearchMapping from '@ion/features/src/search/BaseSearchMappings';
import {
  AttributeOperators,
  AttributeTypes,
  DISABLED,
  ENABLED,
  LookUpSource,
  Mappings,
  SearchFilters,
  SuggestionsType,
  SUSPENDED
} from '@ion/features/src/search/types';
import {useMemberSearchCriteria} from '@ion/features/src/search/useMemberSearchCriteria';
import {useModularSearch} from '@ion/features/src/search/useModularSearch';
import {useCurrentGlobalGroupId} from '../../../hooks/useCurrentGlobalGroupId';
import {resetAssetsSearchQueries, updateAssetsSearchQueries} from './state';

interface UseAssetModularSearchResult {
  getSearchParams: (filters: SearchFilters) => void;
  mappings: BaseSearchMapping;
  filtersSearchCriteria: MemberSearchCriteria;
}

export enum SearchFields {
  NAME = 'name',
  DEVICE_NAME = 'device.name',
  STATUS = 'status',
  LICENSE_NUMBER = 'licenseNumber',
  ASSET_TYPE = 'assetType',
  VIN = 'vin',
  GROUPS = 'groups'
};

// Generates the searchable Asset attributes, options, and labels for modularized search.
const getAssetsMappings = (): Mappings => {
  const {attributes, status} = translate('common:modularsearch', {returnObjects: true});
  const {labels, assetType}  = translate('admin-assets:modularsearch', {returnObjects: true});
  return {
    [SearchFields.NAME] : {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion : SuggestionsType.TEXT,
      label      : labels?.name,
      key        : SearchFields.NAME
    },
    [SearchFields.DEVICE_NAME] : {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion    : SuggestionsType.TEXT,
      label         : labels?.devices,
      key           : SearchFields.DEVICE_NAME,
      hasCollection : true
    },
    [SearchFields.STATUS] : {
      type       : AttributeTypes.SELECT,
      attributes : {
        value : AttributeOperators.IN,
        label : attributes?.in
      },
      options : [
        {
          label : status?.Enabled,
          value : ENABLED
        },
        {
          label : status?.Disabled,
          value : DISABLED
        },
        {
          label : status?.Suspended,
          value : SUSPENDED
        }
      ],
      suggestion : SuggestionsType.SELECTION,
      label      : labels?.status,
      key        : SearchFields.STATUS
    },
    [SearchFields.LICENSE_NUMBER] : {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion : SuggestionsType.TEXT,
      label      : labels?.licensenumber,
      key        : SearchFields.LICENSE_NUMBER
    },
    [SearchFields.ASSET_TYPE] : {
      type       : AttributeTypes.SELECT,
      attributes : {
        value : AttributeOperators.IS,
        label : attributes?.is
      },
      suggestion : SuggestionsType.SELECTION,
      options    : [
        {
          label : assetType?.vehicle,
          value : AssetType.Vehicle.toUpperCase()
        },
        {
          label : assetType?.tool,
          value : AssetType.Tool.toUpperCase()
        },
        {
          label : assetType?.asset,
          value : AssetType.Asset.toUpperCase()
        }
      ],
      label : labels?.assettype,
      key   : SearchFields.ASSET_TYPE
    },
    [SearchFields.VIN] : {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion : SuggestionsType.TEXT,
      label      : labels?.vin,
      key        : SearchFields.VIN
    },
    [SearchFields.GROUPS] : {
      type       : AttributeTypes.SELECT,
      attributes : {
        value : AttributeOperators.IS,
        label : attributes?.is
      },
      suggestion        : SuggestionsType.LOOKUP,
      label             : labels?.groups,
      key               : SearchFields.GROUPS,
      suggestionsSource : LookUpSource.Group
    }
  };
};

/**
 * Custom hook for using modular search in the Assets module.
 */
export const useAssetModularSearch = (onRefresh: () => void, viewSubGroups = false): UseAssetModularSearchResult => {
  const dispatch       = useDispatch();
  const onReset        = useCallback(() => dispatch(resetAssetsSearchQueries()), []);
  const onUpdate       = useCallback((filters: SearchFilters) => dispatch(updateAssetsSearchQueries(filters)), []);
  const globalGroupId  = useCurrentGlobalGroupId();
  const entityMappings = useMemo(() => getAssetsMappings(), []);

  const {
    getSearchParams,
    mappings,
    filters
  } = useModularSearch({
    onReset,
    onUpdate,
    entityMappings,
    onRefresh,
    globalGroupId
  });

  const filtersSearchCriteria = useMemberSearchCriteria({globalGroupId, viewSubGroups, filters, entity: Entity.asset});

  return {getSearchParams, mappings, filtersSearchCriteria};
};

export default useAssetModularSearch;
